// ============================================================
// FILE: src/components/Footer.jsx
// ============================================================

import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";

const Footer = () => {
    return (
        <footer className="bg-[var(--color-dark)] border-t border-[var(--color-border)]/40 pt-16 pb-8 text-[var(--color-surface)]">
            <div className="container-custom">

                {/* Footer Content */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">

                    {/* =================================================
                        COMPANY
                    ================================================= */}

                    <div>
                        <Link
                            to="/"
                            className="flex items-center text-2xl font-bold mb-4 text-[var(--color-surface)]"
                        >
                            <span>Sanwariya Ji</span>
                        </Link>

                        <p className="text-[var(--color-surface)]/80 text-sm leading-relaxed max-w-sm">
                            Building Strength, Creating Spaces. Premium steel
                            fabrication and infrastructure solutions.
                        </p>

                        {/* Social Links */}
                        <div className="flex items-center gap-3 mt-5">

                            {/* Facebook */}
                            <a
                                href="https://www.facebook.com/share/1Cwi3QARrM/?mibextid=wwXIfr"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Facebook"
                                className="w-10 h-10 rounded-full border border-[var(--color-surface)]/20 flex items-center justify-center text-[var(--color-surface)]/80 hover:text-[var(--color-accent)] hover:border-[var(--color-accent)] transition-all duration-300"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="w-5 h-5"
                                >
                                    <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.413c0-3.022 1.791-4.694 4.533-4.694 1.312 0 2.686.236 2.686.236v2.965h-1.514c-1.491 0-1.956.93-1.956 1.885v2.268h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073Z" />
                                </svg>
                            </a>

                            {/* Instagram */}
                            <a
                                href="https://www.instagram.com/sanwariyajisteels?stkn=cWhvdjVqeWdteXY2"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Instagram"
                                className="w-10 h-10 rounded-full border border-[var(--color-surface)]/20 flex items-center justify-center text-[var(--color-surface)]/80 hover:text-[var(--color-accent)] hover:border-[var(--color-accent)] transition-all duration-300"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="w-5 h-5"
                                >
                                    <rect
                                        width="20"
                                        height="20"
                                        x="2"
                                        y="2"
                                        rx="5"
                                        ry="5"
                                    />
                                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                                    <line
                                        x1="17.5"
                                        x2="17.51"
                                        y1="6.5"
                                        y2="6.5"
                                    />
                                </svg>
                            </a>

                        </div>
                    </div>

                    {/* =================================================
                        QUICK LINKS
                    ================================================= */}

                    <div>
                        <h4 className="text-lg font-semibold mb-4 text-[var(--color-surface)]">
                            Quick Links
                        </h4>

                        <ul className="space-y-2 text-sm text-[var(--color-surface)]/80">

                            <li>
                                <Link
                                    to="/about"
                                    className="hover:text-[var(--color-accent)] transition-colors"
                                >
                                    About Us
                                </Link>
                            </li>

                            <li>
                                <Link
                                    to="/services"
                                    className="hover:text-[var(--color-accent)] transition-colors"
                                >
                                    Services
                                </Link>
                            </li>

                            <li>
                                <Link
                                    to="/projects"
                                    className="hover:text-[var(--color-accent)] transition-colors"
                                >
                                    Projects
                                </Link>
                            </li>

                            <li>
                                <Link
                                    to="/contact"
                                    className="hover:text-[var(--color-accent)] transition-colors"
                                >
                                    Contact
                                </Link>
                            </li>

                        </ul>
                    </div>

                    {/* =================================================
                        CONTACT INFO
                    ================================================= */}

                    <div>
                        <h4 className="text-lg font-semibold mb-4 text-[var(--color-surface)]">
                            Contact Info
                        </h4>

                        <ul className="space-y-4 text-sm text-[var(--color-surface)]/80">

                            {/* Address */}
                            <li className="flex items-start gap-3">
                                <MapPin
                                    size={18}
                                    className="text-[var(--color-accent)] mt-0.5 shrink-0"
                                />

                                <span className="leading-6">
                                    Plot No. 31A, Sai Complex,
                                    <br />
                                    Industrial Area, Wajirpur Road,
                                    <br />
                                    Sector 87, Greater Faridabad,
                                    <br />
                                    121002
                                </span>
                            </li>

                            {/* Phone */}
                            <li className="flex items-center gap-3">
                                <Phone
                                    size={18}
                                    className="text-[var(--color-accent)] shrink-0"
                                />

                                <a
                                    href="tel:+918744050001"
                                    className="hover:text-[var(--color-accent)] transition-colors"
                                >
                                    +918744050001
                                </a>
                            </li>

                            {/* Email */}
                            <li className="flex items-center gap-3">
                                <Mail
                                    size={18}
                                    className="text-[var(--color-accent)] shrink-0"
                                />

                                <a
                                    href="mailto:contact@sanwariyajisteels.com"
                                    className="hover:text-[var(--color-accent)] transition-colors break-all"
                                >
                                    contact@sanwariyajisteels.com
                                </a>
                            </li>

                        </ul>
                    </div>

                </div>

                {/* =================================================
                    COPYRIGHT
                ================================================= */}

                <div className="border-t border-slate-700 mt-12 pt-6 text-center text-sm text-slate-400">
                    &copy; {new Date().getFullYear()} Sanwariya Ji Steels &
                    Infrastructure LLP. All rights reserved.
                </div>

            </div>
        </footer>
    );
};

export default Footer;